/**
 * Firmware download section (UpdateFirmware) — both protocols.
 *
 * UpdateFirmware really fetches the image from the CSMS-supplied location; this
 * collapsible panel shows the result (status, size, SHA-256) and lets the tester
 * save the binary to disk for verification. Real download is browser-bounded:
 * a cross-origin host without CORS headers fails → status DownloadFailed.
 */
import { useState } from 'react';
import { useFirmwareStore, type FirmwareStatus } from '@/state/useFirmwareStore';
import { formatDateTime } from '@/lib/format';

const STATUS_TONE: Record<FirmwareStatus, string> = {
  idle: 'bg-slate-700/80 text-slate-300',
  downloading: 'bg-sky-700/80 text-sky-50',
  downloaded: 'bg-primary-base/80 text-primary-on',
  failed: 'bg-rose-700/80 text-rose-50',
  installing: 'bg-amber-700/80 text-amber-50',
  installed: 'bg-blue-700/80 text-blue-50',
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export function FirmwareSection(): JSX.Element {
  const status = useFirmwareStore((s) => s.status);
  const url = useFirmwareStore((s) => s.url);
  const filename = useFirmwareStore((s) => s.filename);
  const sizeBytes = useFirmwareStore((s) => s.sizeBytes);
  const contentType = useFirmwareStore((s) => s.contentType);
  const sha256 = useFirmwareStore((s) => s.sha256);
  const error = useFirmwareStore((s) => s.error);
  const at = useFirmwareStore((s) => s.at);
  const saveToDisk = useFirmwareStore((s) => s.saveToDisk);
  const clear = useFirmwareStore((s) => s.clear);

  const [open, setOpen] = useState(false);
  const canSave = sizeBytes !== null;

  return (
    <section
      className="rounded border border-slate-700 bg-slate-900/60 p-3"
      data-testid="firmware-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="firmware-body"
        className="flex w-full items-center justify-between gap-2 text-left focus:outline-none focus:ring-2 focus:ring-primary-base"
        data-testid="firmware-toggle"
      >
        <span className="flex items-center gap-1.5">
          <svg
            viewBox="0 0 16 16"
            aria-hidden="true"
            className={`h-3 w-3 fill-current text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
          >
            <path d="M5.5 3.5L10.5 8l-5 4.5z" />
          </svg>
          <span className="text-sm font-medium text-slate-100">Firmware (UpdateFirmware)</span>
        </span>
        <span className="flex items-center gap-2">
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${STATUS_TONE[status]}`}
            data-testid="firmware-status"
          >
            {status}
          </span>
          <span className="text-[10px] text-slate-500">{open ? 'hide' : 'expand'}</span>
        </span>
      </button>

      {open ? (
        <div id="firmware-body" className="mt-3 space-y-2 text-xs">
          <p className="text-[11px] text-slate-500">
            UpdateFirmware really fetches the image from the CSMS-supplied URL. Cross-origin hosts
            without CORS headers fail (status DownloadFailed) — serve firmware from a CORS-enabled
            location to download it here.
          </p>

          {status === 'idle' ? (
            <p className="text-[11px] text-slate-500" data-testid="firmware-empty">
              No firmware downloaded yet. Trigger UpdateFirmware from the CSMS.
            </p>
          ) : (
            <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-slate-300">
              <dt className="text-slate-500">URL</dt>
              <dd className="break-all font-mono text-[11px]" data-testid="firmware-url">
                {url ?? '—'}
              </dd>
              <dt className="text-slate-500">File</dt>
              <dd className="font-mono text-[11px]">{filename ?? '—'}</dd>
              <dt className="text-slate-500">Size</dt>
              <dd data-testid="firmware-size">{sizeBytes !== null ? formatBytes(sizeBytes) : '—'}</dd>
              <dt className="text-slate-500">Type</dt>
              <dd className="font-mono text-[11px]">{contentType ?? '—'}</dd>
              <dt className="text-slate-500">SHA-256</dt>
              <dd className="break-all font-mono text-[11px]" data-testid="firmware-sha256">
                {sha256 ?? '—'}
              </dd>
              {at !== null ? (
                <>
                  <dt className="text-slate-500">At</dt>
                  <dd className="text-[11px]">{formatDateTime(at)}</dd>
                </>
              ) : null}
            </dl>
          )}

          {error !== null ? (
            <p
              className="rounded bg-rose-950/60 px-2 py-1 text-[11px] text-rose-300"
              data-testid="firmware-error"
            >
              {error}
            </p>
          ) : null}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={saveToDisk}
              disabled={!canSave}
              title={canSave ? 'Save the downloaded image to disk' : 'No image downloaded'}
              className="rounded bg-primary-hover px-3 py-1 text-primary-on hover:bg-primary-base focus:outline-none focus:ring-2 focus:ring-primary-base disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="firmware-save"
            >
              Save to disk
            </button>
            <button
              type="button"
              onClick={clear}
              disabled={status === 'idle'}
              className="rounded bg-slate-700 px-3 py-1 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="firmware-clear"
            >
              Clear
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
